import { t } from '@lingui/macro'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { Card } from '@/@bothrs-ui/web/components/card/Card'
import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { Markdown } from '@/@bothrs-ui/web/components/markdown/Markdown'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { IconButton } from '@/@bothrs-ui/web/components/primitives/icon-button/IconButton'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H2, H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { mixpanel } from '@/mixpanel'
import { BookingFlowActions, BookingFlowCenteredContent, BookingFlowLayout } from './BookingFlowLayout'

import type { ContactLocation, FlowState, Specialisation } from '../types/booking.type'

type NoDigitalBookingContentProps = {
  flowState: FlowState
  data: {
    specialisation: Specialisation
  }
  isLoading?: boolean
  description?: string
}

export const NoDigitalBookingContent = ({ flowState, data, isLoading, description }: NoDigitalBookingContentProps) => {
  const theme = useTheme()
  const navigate = useNavigate()

  const handleClose = () => {
    mixpanel.track('booking_flow_closed', {
      step: 'no_digital_booking',
      withTriage: !!flowState.triagePath?.length,
    })
    navigate('/home', { replace: true })
  }

  const handlePhoneClick = (phoneNumber: string, locationName: string) => {
    mixpanel.track('booking_flow_no_digital_booking_phone_clicked', {
      phoneNumber,
      locationName,
    })
    window.open(`tel:${phoneNumber}`, '_self')
  }

  const renderContactLocation = (location: ContactLocation) => (
    <Card key={location.id}>
      <FlexColumn gap={theme.UI.SpacingPx.Space3}>
        <FlexColumn gap={theme.UI.SpacingPx.Space1}>
          <H3 noMargin>{location.name}</H3>
          <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
            {location.profession}
          </Span>
        </FlexColumn>
        <Line />
        <FlexRow justifyContent='space-between' alignItems='center'>
          <Span type='BodyMediumRegular'>{location.phoneNumberDisplay || location.phoneNumber}</Span>
          <IconButton
            variant='Secondary'
            icon={['fal', 'phone']}
            size={'small'}
            onClick={() => handlePhoneClick(location.phoneNumber, location.name)}
          />
        </FlexRow>
      </FlexColumn>
    </Card>
  )

  return (
    <BookingFlowLayout>
      <BookingFlowCenteredContent>
        <H2 noMargin>
          {t({
            id: 'appointments.book.no_digital_booking.title',
            comment: 'No digital booking title',
            message: 'Je kan voor deze specialisatie online nog geen afspraak boeken',
          })}
        </H2>
        {description ? (
          <Markdown content={description} typography='BodyMediumRegular' />
        ) : (
          <Span type='BodyMediumRegular'>
            {t({
              id: 'appointments.book.no_digital_booking.description',
              comment: 'No digital booking description',
              message: 'Als je een afspraak wil maken bij {specialisation}, neem contact op met het ziekenhuis.',
              values: {
                specialisation: data.specialisation.title.nl || 'Onbekende specialisatie',
              },
            })}
          </Span>
        )}

        {flowState?.contactLocations && flowState.contactLocations.length > 0 ? (
          flowState.contactLocations.sort((a, b) => a.id - b.id).map(renderContactLocation)
        ) : (
          <Card>
            <FlexColumn gap={theme.UI.SpacingPx.Space3}>
              <FlexColumn gap={theme.UI.SpacingPx.Space1}>
                <H3 noMargin>
                  {t({
                    id: 'appointments.book.contact.noData.title',
                    comment: 'No contact data title',
                    message: 'Geen contactgegevens beschikbaar',
                  })}
                </H3>
                <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
                  {flowState?.specialisation?.title?.nl || 'Onbekende specialisatie'}
                </Span>
              </FlexColumn>
              <Line />
              <Span type='BodyMediumRegular'>
                {t({
                  id: 'appointments.book.contact.noData.description',
                  comment: 'No contact data description',
                  message: 'Neem contact op via de algemene lijn van het ziekenhuis.',
                })}
              </Span>
            </FlexColumn>
          </Card>
        )}
      </BookingFlowCenteredContent>
      <BookingFlowActions>
        <Button variant='Primary' onClick={handleClose} disabled={isLoading} isLoading={isLoading}>
          {t({ id: 'globals.close', message: 'Sluiten' })}
        </Button>
      </BookingFlowActions>
    </BookingFlowLayout>
  )
}
